import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Ban,
  Check,
  ShieldCheck,
  Trash2,
  Undo2,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

function InfoField({ label, value, mono = false, testId }) {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/70 px-3 py-2.5">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
        {label}
      </div>
      <div
        className={`mt-1 text-sm text-slate-100 ${mono ? "font-mono" : ""}`}
        data-testid={testId}
      >
        {value ?? "—"}
      </div>
    </div>
  );
}

export default function AdminTenantDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmName, setConfirmName] = useState("");
  const [showDelete, setShowDelete] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/platform/tenants/${id}`);
      setTenant(data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : d?.message || "Failed to load tenant");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  async function patchTenant(payload, successMsg) {
    setSaving(true);
    try {
      const { data } = await api.patch(`/platform/tenants/${id}`, payload);
      setTenant((prev) => ({ ...prev, ...data }));
      toast.success(successMsg);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Update failed");
    } finally {
      setSaving(false);
    }
  }

  async function suspend() {
    setSaving(true);
    try {
      await api.post(`/platform/tenants/${id}/suspend`);
      toast.success("Tenant suspended");
      load();
    } catch (err) {
      toast.error("Failed to suspend");
    } finally {
      setSaving(false);
    }
  }

  async function activate() {
    setSaving(true);
    try {
      await api.post(`/platform/tenants/${id}/activate`);
      toast.success("Tenant activated");
      load();
    } catch (err) {
      toast.error("Failed to activate");
    } finally {
      setSaving(false);
    }
  }

  async function doDelete() {
    setSaving(true);
    try {
      await api.delete(`/platform/tenants/${id}`, {
        data: { confirm: confirmName },
      });
      toast.success("Tenant deleted");
      nav("/admin/tenants", { replace: true });
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Delete failed");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div
        className="rounded-2xl border border-slate-800 bg-slate-900/70 p-10 text-center text-slate-400"
        data-testid="admin-tenant-loading"
      >
        Loading tenant…
      </div>
    );
  }
  if (error || !tenant) {
    return (
      <div className="space-y-4">
        <Link
          to="/admin/tenants"
          className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" /> Back to tenants
        </Link>
        <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-6 text-sm text-red-200">
          {error || "Tenant not found."}
        </div>
      </div>
    );
  }

  const canDeleteConfirm = confirmName.trim() === (tenant.name || "").trim();

  return (
    <div className="space-y-6" data-testid="admin-tenant-detail-page">
      <Link
        to="/admin/tenants"
        className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-white"
        data-testid="admin-tenant-back"
      >
        <ArrowLeft className="h-4 w-4" /> Back to tenants
      </Link>

      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-white sm:text-4xl">
            {tenant.name}
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
            <span
              className={
                tenant.is_active
                  ? "rounded-md bg-emerald-500/15 px-2 py-0.5 text-emerald-300"
                  : "rounded-md bg-red-500/15 px-2 py-0.5 text-red-300"
              }
              data-testid="admin-tenant-status"
            >
              {tenant.is_active ? "Active" : "Suspended"}
            </span>
            {tenant.is_deleted && (
              <span className="rounded-md bg-slate-800 px-2 py-0.5 text-slate-300">
                deleted
              </span>
            )}
            <span className="rounded-md bg-slate-800 px-2 py-0.5 uppercase tracking-wide text-slate-300">
              {tenant.subscription_plan}
            </span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {tenant.is_active ? (
            <button
              onClick={suspend}
              disabled={saving}
              data-testid="admin-tenant-suspend"
              className="inline-flex items-center gap-2 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-200 hover:bg-red-500/20"
            >
              <Ban className="h-4 w-4" /> Suspend
            </button>
          ) : (
            <button
              onClick={activate}
              disabled={saving}
              data-testid="admin-tenant-activate"
              className="inline-flex items-center gap-2 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-200 hover:bg-emerald-500/20"
            >
              <Undo2 className="h-4 w-4" /> Activate
            </button>
          )}
          {!tenant.is_deleted && (
            <button
              onClick={() => setShowDelete(true)}
              data-testid="admin-tenant-delete-btn"
              className="inline-flex items-center gap-2 rounded-lg border border-red-500/40 bg-transparent px-3 py-2 text-sm text-red-300 hover:bg-red-500/10"
            >
              <Trash2 className="h-4 w-4" /> Delete
            </button>
          )}
        </div>
      </header>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <InfoField label="Users" value={tenant.user_count} testId="admin-tenant-users" />
        <InfoField label="Invoices" value={tenant.invoice_count} testId="admin-tenant-invoices" />
        <InfoField label="Payments" value={tenant.payments_count} testId="admin-tenant-payments" />
        <InfoField label="Default currency" value={tenant.default_currency} />
        <InfoField label="Default language" value={tenant.default_language} />
        <InfoField label="Admin email" value={tenant.admin_email} mono />
        <InfoField label="Tenant ID" value={tenant.id} mono />
        <InfoField
          label="Created"
          value={tenant.created_at ? new Date(tenant.created_at).toLocaleString() : "—"}
        />
      </div>

      <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-5">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-slate-300">
          Subscription plan
        </h2>
        <div className="flex flex-wrap gap-2">
          {["free", "pro", "enterprise"].map((p) => {
            const active = tenant.subscription_plan === p;
            return (
              <button
                key={p}
                onClick={() => {
                  const body = { subscription_plan: p };
                  if (p !== "free") {
                    const days = window.prompt("Renewal days from now (default 30):", "30");
                    if (days === null) return;
                    const d = Math.max(1, Number(days) || 30);
                    const ends = new Date(Date.now() + d * 86400000).toISOString();
                    body.plan_ends_at = ends;
                  } else {
                    body.plan_ends_at = null;
                  }
                  patchTenant(body, `Plan set to ${p}`);
                }}
                disabled={saving || active}
                data-testid={`admin-tenant-plan-${p}`}
                className={[
                  "inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm capitalize transition-colors",
                  active
                    ? "border-amber-500/50 bg-amber-500/15 text-amber-300"
                    : "border-slate-700 bg-slate-950 text-slate-300 hover:border-slate-600 hover:text-white",
                ].join(" ")}
              >
                {active && <Check className="h-4 w-4" />}
                {p}
              </button>
            );
          })}
        </div>
        {tenant.plan_ends_at && (
          <div className="mt-3 text-xs text-slate-400" data-testid="admin-tenant-plan-ends">
            <strong className="text-slate-200">Plan ends:</strong>{" "}
            {new Date(tenant.plan_ends_at).toLocaleString()}
            {" · "}
            {Math.round((new Date(tenant.plan_ends_at) - Date.now()) / 86400000)} days
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-5">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-slate-300">
          Users
        </h2>
        <div className="mobile-cards overflow-hidden rounded-xl border border-slate-800">
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-xs uppercase tracking-wider text-slate-400">
              <tr>
                <th className="px-4 py-3 text-start">Name</th>
                <th className="px-4 py-3 text-start">Email</th>
                <th className="px-4 py-3 text-start">Role</th>
                <th className="px-4 py-3 text-start">Status</th>
              </tr>
            </thead>
            <tbody data-testid="admin-tenant-users-table">
              {(tenant.users || []).map((u) => (
                <tr key={u.id} className="border-t border-slate-800" data-testid={`admin-tenant-user-${u.email}`}>
                  <td className="px-4 py-3 text-slate-100" data-label="Name">
                    {u.name}
                  </td>
                  <td className="px-4 py-3 text-slate-300" data-label="Email">
                    <span className="font-mono">{u.email}</span>
                  </td>
                  <td className="px-4 py-3 capitalize text-slate-300" data-label="Role">
                    {u.role}
                  </td>
                  <td className="px-4 py-3" data-label="Status">
                    <span
                      className={
                        u.is_active
                          ? "text-xs text-emerald-300"
                          : "text-xs text-slate-500"
                      }
                    >
                      {u.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                </tr>
              ))}
              {(!tenant.users || tenant.users.length === 0) && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-slate-500">
                    No users found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          data-testid="admin-tenant-delete-modal"
        >
          <div className="w-full max-w-md rounded-2xl border border-red-500/30 bg-slate-950 p-6 shadow-2xl">
            <div className="mb-4 flex items-start gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-500/15 text-red-300">
                <Trash2 className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Delete tenant</h3>
                <p className="mt-1 text-sm text-slate-400">
                  This soft-deletes the tenant and deactivates all its users.
                  Type the tenant name exactly to confirm:{" "}
                  <span className="font-mono text-slate-200">{tenant.name}</span>
                </p>
              </div>
            </div>
            <input
              type="text"
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              placeholder={tenant.name}
              data-testid="admin-tenant-delete-confirm-input"
              className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2.5 text-sm text-white placeholder:text-slate-600 focus:border-red-500 focus:outline-none"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowDelete(false);
                  setConfirmName("");
                }}
                className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-300 hover:bg-slate-800"
                data-testid="admin-tenant-delete-cancel"
              >
                Cancel
              </button>
              <button
                onClick={doDelete}
                disabled={!canDeleteConfirm || saving}
                data-testid="admin-tenant-delete-confirm"
                className="rounded-lg bg-red-500 px-3 py-2 text-sm font-medium text-white hover:bg-red-400 disabled:opacity-40"
              >
                Delete tenant
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4 text-xs text-slate-500">
        <ShieldCheck className="me-1 inline h-3 w-3 align-text-bottom text-amber-400" />
        You are acting as a platform super admin. All actions are audited.
      </div>
    </div>
  );
}
